import type { UseCaseArtworkId } from "@replohq/lattice/blocks/workflow-art/useCaseArtwork";
import type { LaunchWorkflowId } from "schemas/generated/workflowLaunch";
import type { z } from "zod";

import * as React from "react";

import { GLASS_PANEL_CLASS } from "@replohq/lattice/blocks/get-started-card/glassSurface";
import { UseCaseWorkflowArt } from "@replohq/lattice/blocks/workflow-art/UseCaseWorkflowArt";
import { WorkflowLaunchChecklist } from "@replohq/lattice/blocks/workflow-checklist/WorkflowLaunchChecklist";
import { WorkflowStep } from "@replohq/lattice/blocks/workflow-checklist/WorkflowStep";
import { cn } from "@replohq/lattice/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { getPublisherUrl } from "@v2/utils/env";
import { workflowPublicInputsSchema } from "schemas/workflowLaunch";
import { LAUNCH_WORKFLOWS } from "schemas/workflowLaunchDefinition";

import rollingHills from "../../../../../replo-agent/src/agent-ui/assets/activation/wallpaper-rolling-hills.png";
import softWaves from "../../../../../replo-agent/src/agent-ui/assets/activation/wallpaper-soft-waves.png";

const previewInputs = {
  sourceUrl: "",
  productDescription: "",
  conversionGoal: "Add to cart",
  productId: null,
};

export function WorkflowAuthFormHeading({
  workflowTitle,
  authRoute,
}: {
  workflowTitle: string;
  authRoute: "signup" | "login";
}) {
  const isSignup = authRoute === "signup";
  const description = isSignup
    ? "Continue where you left off and finish setting up your workflow."
    : "Sign in to pick up your workflow where you left off.";

  return (
    <div className="mb-6 flex flex-col gap-1.5">
      <div className="flex min-w-0 flex-col gap-1.5">
        <h1 className="text-xl font-semibold leading-snug text-default">
          {isSignup ? "Create your account" : "Sign in"}
        </h1>
        <p className="text-sm leading-5 text-muted">{description}</p>
        <span className="text-sm text-muted lg:sr-only">{workflowTitle}</span>
      </div>
    </div>
  );
}

export function WorkflowAuthPreview({
  workflowId,
  workflowToken,
  initialInputs,
  marketingWorkflow,
}: {
  workflowId: LaunchWorkflowId | null;
  workflowToken: string | null;
  initialInputs: z.infer<typeof workflowPublicInputsSchema> | null;
  marketingWorkflow: {
    slug: UseCaseArtworkId;
    title: string;
    description: string;
    steps: { id: string; label: string; value: string }[];
  } | null;
}) {
  const workflow =
    marketingWorkflow ?? (workflowId ? LAUNCH_WORKFLOWS[workflowId] : null);
  const wallpaper = workflowId === "ad-to-lander" ? rollingHills : softWaves;
  const { data: previewData } = useQuery({
    queryKey: ["workflow-public-preview", workflowToken],
    enabled: Boolean(workflowToken && !initialInputs),
    initialData: initialInputs ?? undefined,
    retry: 2,
    queryFn: async () => {
      const response = await fetch(
        `${getPublisherUrl()}/api/v1/marketing/workflow-continuations/preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: workflowToken }),
        },
      );
      if (!response.ok) {
        return null;
      }
      return workflowPublicInputsSchema.parse(await response.json());
    },
  });
  const inputs = { ...previewInputs, ...previewData };
  const artworkId = marketingWorkflow?.slug ?? workflowId;
  if (!workflow || !artworkId) {
    return null;
  }

  return (
    <div className="relative size-full overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 bg-brand-seashell" />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-80"
        style={{ backgroundImage: `url(${wallpaper})` }}
      />
      <div className="relative z-10 flex size-full items-center justify-center p-8">
        <div
          ref={(element) => {
            element?.setAttribute("inert", "");
          }}
          aria-hidden="true"
          className={cn(
            GLASS_PANEL_CLASS,
            "pointer-events-none mx-auto flex max-h-full w-full max-w-2xl select-none flex-col overflow-y-auto rounded-3xl bg-sheet/95 p-2 text-left text-default",
          )}
        >
          <header className="flex min-w-0 flex-col items-center gap-4 px-5 py-4">
            <div className="relative aspect-[4/3] w-48 shrink-0 overflow-hidden rounded-xl">
              <UseCaseWorkflowArt slug={artworkId} />
            </div>
            <div className="flex w-full min-w-0 max-w-lg flex-col gap-1 text-center">
              <h2 className="break-words text-xl font-semibold leading-tight text-default">
                {workflow.title}
              </h2>
              <p className="break-words text-sm leading-6 text-muted">
                {workflow.description}
              </p>
            </div>
          </header>
          <div className="mt-2 flex min-w-0 flex-col gap-4 border-t-0.5 border-border px-5 pb-3 pt-2">
            {workflowId ? (
              <WorkflowLaunchChecklist
                workflowId={workflowId}
                inputs={inputs}
                onChange={() => undefined}
                projectId={null}
                shopifyConnected={false}
                productAvailable={false}
                onContinue={() => undefined}
                busy={false}
                readOnly
                hideContinue
              />
            ) : (
              <div className="min-w-0">
                {marketingWorkflow?.steps.map((step, index) => (
                  <WorkflowStep
                    key={step.id}
                    label={step.label}
                    stepNumber={index + 1}
                    done={Boolean(step.value.trim())}
                    open={Boolean(step.value.trim())}
                    onOpenChange={() => undefined}
                  >
                    <div className="rounded-lg border-0.5 border-border bg-surface px-3 py-2 text-sm whitespace-pre-wrap break-words text-default [overflow-wrap:anywhere]">
                      {step.value}
                    </div>
                  </WorkflowStep>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
